import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Cluster, EmptyState, Spinner } from "@salesapp/ui";
import { useAccess } from "../../../shell/AccessProvider";
import { useLocale } from "../../../shell/LocaleProvider";
import { SearchBar, StatusSlicer, statusLabel } from "../SearchBar";
import { dashboardDesignerApi } from "./api";
import type { DashboardTemplate } from "./api";
import "./dashboard_designer.css";
import "./designers.css";

export function DashboardListPage({ orgId, designerBasePath = "/apps/crm/dashboard-designer" }: {
  orgId: string;
  designerBasePath?: string;
}) {
  const { can } = useAccess();
  const { formatDateTime } = useLocale();
  const navigate = useNavigate();
  const canDesign = can("crm.dashboard.design", "design") || can("crm.dashboard.design");
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(async () => {
    if (!orgId) return;
    setError("");
    try {
      setTemplates(await dashboardDesignerApi.list(orgId));
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <Spinner />;

  const dashboardType = (t: DashboardTemplate) => (t.is_default ? "default" : t.kind || "custom");
  const typeOptions = Array.from(new Set(templates.map(dashboardType))).sort();
  const needle = search.trim().toLowerCase();
  const visibleTemplates = templates.filter(
    (t) => (!typeFilter || dashboardType(t) === typeFilter) && (!needle || t.name.toLowerCase().includes(needle)),
  );

  const newDashboard = async (template?: "sales") => {
    if (!orgId) return;
    try {
      const created = await dashboardDesignerApi.create(orgId, template === "sales" ? "Sales Dashboard" : "New Dashboard", template);
      navigate(`${designerBasePath}/${created.id}/designer`);
    } catch (e) { setError((e as Error).message); }
  };

  const removeDashboard = async (t: DashboardTemplate) => {
    if (!orgId) return;
    if (!confirm(`Delete "${t.name}"?\n\nThis dashboard will sit in the recycle bin until an admin restores it or it expires.`)) return;
    try {
      await dashboardDesignerApi.remove(orgId, t.id);
      await load();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="designer">
      <Cluster justify="space-between" align="center">
        <h1>Dashboards</h1>
        {canDesign && (
          <Cluster gap={2}>
            <Button
              variant="secondary"
              title="Create a dashboard pre-filled with sales charts (pipeline, closed vs target, deals by stage, and similar). Opens the designer next."
              onClick={() => newDashboard("sales")}
            >
              New Sales Dashboard
            </Button>
            <Button
              title="Create a blank dashboard and open the designer so you can add your own sections and charts."
              onClick={() => newDashboard()}
            >
              New Dashboard
            </Button>
          </Cluster>
        )}
      </Cluster>
      {error && <p role="alert">{error}</p>}
      {templates.length > 0 && (
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search dashboards…"
            label="Search dashboards"
            hint="Search by dashboard name."
          />
          <StatusSlicer
            value={typeFilter}
            onChange={setTypeFilter}
            options={typeOptions}
            label="Filter by dashboard type"
            labelFor={statusLabel}
          />
        </div>
      )}
      {templates.length === 0 ? (
        <EmptyState title="No dashboards yet" description={canDesign ? "Create your first dashboard to chart CRM data." : "No dashboards have been created yet."} />
      ) : visibleTemplates.length === 0 ? (
        <EmptyState title="No dashboards match" description="Try a different search or type." />
      ) : (
        <div className="ui-table__scroll">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Dashboard</th>
                <th>Sections</th>
                <th>Modified</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleTemplates.map((t) => (
                <tr key={t.id} className="ui-table__row--clickable" onClick={() => navigate(`${designerBasePath}/${t.id}`)}>
                  <td><strong>{t.name}</strong></td>
                  <td>{t.layout_json?.sections?.length ?? 0}</td>
                  <td>{t.updated_at ? formatDateTime(t.updated_at) : "—"}</td>
                  <td>
                    <Cluster gap={2} justify="end" onClick={e => e.stopPropagation()}>
                      <Button
                        variant="secondary"
                        title={`Open “${t.name}” in view mode to see live charts. Does not open the designer.`}
                        onClick={() => navigate(`${designerBasePath}/${t.id}`)}
                      >
                        View
                      </Button>
                      {canDesign && (
                        <>
                          <Button
                            variant="secondary"
                            title={`Open “${t.name}” in the designer to change layout, charts, and sharing.`}
                            onClick={() => navigate(`${designerBasePath}/${t.id}/designer`)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            title={`Move “${t.name}” to the recycle bin. An admin can restore it later, or it expires.`}
                            onClick={() => removeDashboard(t)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </Cluster>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
